import useSWR from 'swr';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Global SWR configuration
export const swrConfig = {
  revalidateOnFocus: true,      // Revalidate when window regains focus
  revalidateOnReconnect: true,  // Revalidate when network reconnects
  refreshInterval: 0,           // No polling by default
  dedupingInterval: 2000,       // Dedupe requests within 2 seconds
};

// Generic fetcher function
const fetcher = async (url, options = {}) => {
  const response = await fetch(url, options);
  if (!response.ok) {
    const error = new Error('API request failed');
    error.status = response.status;
    throw error;
  }
  return response.json();
};

// Authenticated fetcher
const authFetcher = async (url, token) => {
  const headers = { 'Content-Type': 'application/json' };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const response = await fetch(url, {
    headers,
    credentials: 'include',  // also send session cookie for Google OAuth users
  });
  if (!response.ok) {
    const error = new Error('API request failed');
    error.status = response.status;
    throw error;
  }
  return response.json();
};

// Hook for fetching restaurants
export const useRestaurants = () => {
  const { data, error, isLoading, mutate } = useSWR(
    `${API_URL}/api/restaurants`,
    fetcher,
    swrConfig
  );
  
  return {
    restaurants: data || [],
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Hook for fetching a single restaurant
export const useRestaurant = (restaurantId) => {
  const { data, error, isLoading, mutate } = useSWR(
    restaurantId ? `${API_URL}/api/restaurants/${restaurantId}` : null,
    fetcher,
    swrConfig
  );
  
  return {
    restaurant: data,
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Hook for fetching restaurant menu
export const useRestaurantMenu = (restaurantId) => {
  const { data, error, isLoading, mutate } = useSWR(
    restaurantId ? `${API_URL}/api/restaurants/${restaurantId}/menu` : null,
    fetcher,
    swrConfig
  );
  
  return {
    menuItems: data || [],
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Hook for fetching service categories
export const useServiceCategories = () => {
  const { data, error, isLoading, mutate } = useSWR(
    `${API_URL}/api/service-categories`,
    fetcher,
    swrConfig
  );
  
  return {
    categories: data || [],
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Hook for fetching ice products
export const useIceProducts = () => {
  const { data, error, isLoading, mutate } = useSWR(
    `${API_URL}/api/ice-products`,
    fetcher,
    swrConfig
  );
  
  return {
    products: data || [],
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Hook for fetching shishas
export const useShishas = () => {
  const { data, error, isLoading, mutate } = useSWR(
    `${API_URL}/api/shishas`,
    fetcher,
    swrConfig
  );
  
  return {
    shishas: data || [],
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Hook for fetching shisha flavors
export const useShishaFlavors = () => {
  const { data, error, isLoading, mutate } = useSWR(
    `${API_URL}/api/shisha-flavors`,
    fetcher,
    swrConfig
  );
  
  return {
    flavors: data || [],
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Hook for fetching user orders (authenticated via JWT or session cookie)
export const useUserOrders = (token, isAuthenticated = false) => {
  const enabled = !!(token || isAuthenticated);
  const { data, error, isLoading, mutate } = useSWR(
    enabled ? [`${API_URL}/api/orders`, token || ''] : null,
    ([url, t]) => authFetcher(url, t),
    swrConfig
  );
  
  return {
    orders: data || [],
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Hook for fetching user shisha rentals (authenticated via JWT or session cookie)
export const useUserRentals = (token, isAuthenticated = false) => {
  const enabled = !!(token || isAuthenticated);
  const { data, error, isLoading, mutate } = useSWR(
    enabled ? [`${API_URL}/api/shisha-rentals`, token || ''] : null,
    ([url, t]) => authFetcher(url, t),
    swrConfig
  );
  
  return {
    rentals: data || [],
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Hook for fetching delivery zones
export const useDeliveryZones = () => {
  const { data, error, isLoading, mutate } = useSWR(
    `${API_URL}/api/delivery-zones`,
    fetcher,
    swrConfig
  );
  
  return {
    zones: data || [],
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Hook for fetching platform settings
export const useSettings = () => {
  const { data, error, isLoading, mutate } = useSWR(
    `${API_URL}/api/settings`,
    fetcher,
    swrConfig
  );
  
  return {
    settings: data || {},
    isLoading,
    isError: error,
    refresh: mutate,
  };
};

// Export mutate function for manual cache invalidation
export { mutate } from 'swr';
